import axios from 'axios';
import {
  InternalChromeConfig,
  ADS_PRODUCT,
  SideNavLink,
} from '@amzn/aac-chrome-internal-api';
import { DEVICE_TYPE, PAGE_LAYOUT_TYPE, VIEW_TYPE } from 'src/app/slice';
import { logger } from './logger';
import { CurrencySwitcherPublisher } from './metrics';
import { ATC_EXTERNAL_TOGGLE } from '../constants';

export const isStandardPageLayoutType = (
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
): boolean => {
  return (
    !!pageLayoutType &&
    (pageLayoutType === PAGE_LAYOUT_TYPE.STANDARD ||
      pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD)
  );
};

export const shouldRenderMenu = (
  viewType: VIEW_TYPE,
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
): boolean => {
  if (viewType === VIEW_TYPE.DESKTOP) {
    return (
      pageLayoutType !== PAGE_LAYOUT_TYPE.LIGHT &&
      pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS &&
      pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER
    );
  } else {
    return (
      pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS &&
      pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER
    );
  }
};

export const currencyofViewSwitch = async (
  currentCurrency: string,
  newCurrency: string,
): Promise<void> => {
  const startTime = performance.now();
  const refTag = `ref=cov_${currentCurrency}_${newCurrency}`;
  const postURL = `/billing/currencyOfView/change?${refTag}`;

  const postData = {
    newCurrencyOfView: newCurrency,
    oldCurrencyOfView: currentCurrency,
  };

  try {
    const currencyOfViewChange = await axios(postURL, {
      method: 'POST',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      data: postData,
    });

    if (currencyOfViewChange) {
      CurrencySwitcherPublisher.publishCounterMonitor(
        'currecyOfViewSwitchSuccessCount',
        1,
      );

      window.location.reload();
    }
  } catch (error: any) {
    logger.error(
      'Error occurred while switching currency of view in AACChrome',
      error,
    );
    CurrencySwitcherPublisher.publishCounterMonitor(
      'currecyOfViewSwitchFailureCount',
      1,
    );
  } finally {
    CurrencySwitcherPublisher.publishTimerMonitor(
      'time',
      performance.now() - startTime,
    );
  }
};

export const showInternalView = (
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
  isInternalView = false,
): boolean => {
  const isExternalToggleOn =
    window.sessionStorage.getItem(ATC_EXTERNAL_TOGGLE) === 'true';
  return (
    (pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD || isInternalView) &&
    !isExternalToggleOn
  );
};

export const getTrimInternalChromeConfig = ():
  | InternalChromeConfig
  | undefined => {
  try {
    return window.AACChrome.InternalAPI.InternalChromeConfigHandler.getInstance()
      .internalChromeConfig;
  } catch (e) {
    return {} as InternalChromeConfig;
  }
};

export const isMobileForTrim = (): boolean => {
  const internalChromeConfig = getTrimInternalChromeConfig();
  return (
    internalChromeConfig?.deviceType === DEVICE_TYPE.MOBILE &&
    internalChromeConfig?.mobileWebSupport === 'ENABLED' &&
    !internalChromeConfig?.forceDesktopSite
  );
};

/**
 * Converts a string to kebab-case format.
 *
 * @param {string} str - The input string to convert
 * @returns {string} The kebab-case formatted string
 *
 * @description
 * This function:
 * 1. Converts camelCase by adding hyphens between lowercase and uppercase letters
 * 2. Replaces spaces and underscores with hyphens
 * 3. Converts the entire string to lowercase
 */
export const toKebabCase = (str: string): string => {
  return str
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Split camelCase like 'aA'
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2') // Split consecutive capitals like 'ABc'
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2') // Split numbers and letters
    .replace(/[^a-zA-Z0-9]+/g, '-') // Replace any non-alphanumeric char with hyphen
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading and trailing hyphens
    .toLowerCase();
};

/**
 * Converts a string to sentence case (capitalizes only the first word).
 *
 * @param str - The input string to be converted
 * @returns The input string with only the first character capitalized and the rest in lowercase
 *
 * @example
 * ```ts
 * toSentenceCase('HELLO WORLD') // 'Hello world'
 * toSentenceCase('welcome TO amazon') // 'Welcome to amazon'
 * toSentenceCase(' spaces ') // 'Spaces'
 * toSentenceCase('') // ''
 * ```
 */
export const toSentenceCase = (str: string): string => {
  const trimmed = str.trim();
  if (!trimmed) return '';

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

export const CAMPAIGN_CREATION_CONFIGS = [
  {
    id: 'aac-menu-item-campaign-creation-sp',
    appName: 'CampaignCreationSP',
    adsProduct: ADS_PRODUCT.SP,
    label: 'sidenav-create-campaign-sp',
    url: '/cb/sp',
  },
  {
    id: 'aac-menu-item-campaign-creation-sb',
    appName: 'CampaignCreationSB',
    adsProduct: ADS_PRODUCT.SB,
    label: 'sidenav-create-campaign-sb',
    url: '/cb/sb',
  },
  {
    id: 'aac-menu-item-campaign-creation-sd',
    appName: 'CampaignCreationSD',
    adsProduct: ADS_PRODUCT.SD,
    label: 'sidenav-create-campaign-sd',
    url: '/cb/sd',
  },
  {
    id: 'aac-menu-item-campaign-creation-stv',
    appName: 'CampaignCreationSTV',
    adsProduct: ADS_PRODUCT.SPOT,
    label: 'sidenav-create-campaign-stv',
    url: '/create/settings/st',
  },
  {
    id: 'aac-menu-item-campaign-creation-dsp',
    appName: 'CampaignCreationDSP',
    adsProduct: ADS_PRODUCT.DSP,
    label: 'sidenav-create-campaign-dsp',
    url: '/dsp/$ENTITY_ID$/advertisers/$ADVERTISER_ID$/orders',
  },
  {
    id: 'aac-menu-item-campaign-creation-ctv',
    appName: 'CampaignCreationCTV',
    adsProduct: ADS_PRODUCT.DSP_TV,
    label: 'sidenav-create-campaign-ctv',
    url: '/dsp/$ENTITY_ID$/advertisers/$ADVERTISER_ID$/orders/ctv/new',
  },
];

const ADS_PRODUCT_TO_CONFIG = new Map(
  CAMPAIGN_CREATION_CONFIGS.map((config) => [config.adsProduct, config]),
);

export const buildCampaignCreationLinks = (
  eligibleProductsMap: Map<ADS_PRODUCT, string>,
  translations: Map<string, string>,
): SideNavLink[] => {
  const links: SideNavLink[] = [];
  for (const [adsProduct, url] of eligibleProductsMap) {
    const config = ADS_PRODUCT_TO_CONFIG.get(adsProduct);
    if (config) {
      links.push({
        id: config.id,
        appName: config.appName,
        link: { label: translations.get(config.label) || '', url },
        sidebarItemType: 'APPLICATION' as const,
      });
    }
  }
  return links;
};
